import { z } from 'zod';

// ---------------------------------------------------------------------------
// Password rules (shared across login, register, reset)
// ---------------------------------------------------------------------------

export const PasswordSchema = z
  .string()
  .min(8, 'At least 8 characters required')
  .regex(/[A-Z]/, 'Must include an uppercase letter')
  .regex(/[a-z]/, 'Must include a lowercase letter')
  .regex(/[0-9]/, 'Must include a number')
  .regex(/[^A-Za-z0-9]/, 'Must include a special character');

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

export const LoginRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

// ---------------------------------------------------------------------------
// Register form (UI layer — includes UI-only fields)
// ---------------------------------------------------------------------------

export const RegisterFormSchema = z
  .object({
    first_name: z.string().min(2, 'At least 2 characters'),
    last_name: z.string().min(2, 'At least 2 characters'),
    email: z.string().email('Invalid email address'),
    phone: z.string().min(4, 'Enter a valid phone number').max(20, 'Phone number too long'),
    password: PasswordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    invitation_code: z
      .string()
      .regex(/^[A-Za-z0-9]{6}$/, 'Must be 6 alphanumeric characters')
      .optional()
      .or(z.literal('')),
    country: z.string().min(1, 'Please select a country'),
    termsAccepted: z.boolean().refine((v) => v === true, {
      message: 'You must accept the terms to continue',
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

// ---------------------------------------------------------------------------
// Organization setup (step 2 after register when no invitation code)
// ---------------------------------------------------------------------------

export const OrgSetupSchema = z.object({
  name: z.string().min(1, 'Organization name is required'),
  website: z.string().url('Please enter a valid URL'),
  address: z.string().min(20, 'At least 20 characters').max(100, 'Too long'),
  contact_phone_number: z.string().min(4, 'Enter a valid phone number').max(20),
  contact_mail: z.string().email('Invalid email address'),
  admin_name: z.string().min(3, 'At least 3 characters'),
  admin_email: z.string().email('Invalid email address'),
  country: z.string().min(1, 'Please select a country'),
});

// ---------------------------------------------------------------------------
// Forgot password
// ---------------------------------------------------------------------------

export const ForgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

// ---------------------------------------------------------------------------
// Reset password (after clicking the emailed link)
// ---------------------------------------------------------------------------

export const CheckResetTokenRequestSchema = z.object({
  token: z.string().min(1, 'Missing reset token'),
});

export const ResetPasswordFormSchema = z
  .object({
    password: PasswordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

// ---------------------------------------------------------------------------
// User
// ---------------------------------------------------------------------------

export const UserSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  // `/me` omits the key when the user has no organization.
  organization: z.string().nullish(),
  is_email_verified: z.boolean(),
  active: z.boolean(),
  expiry: z.string().nullable(),
  credits: z.number().optional().default(0),
  is_admin: z.boolean().optional().default(false),
  is_demo: z.boolean().optional().default(false),
  org_role: z.enum(['org_admin', 'model_creator', 'member']).optional(),
  created_at: z.string(),
});

// ---------------------------------------------------------------------------
// Login response (data field of the API envelope)
// ---------------------------------------------------------------------------

export const LoginResponseSchema = z.object({
  access_token: z.string(),
  is_demo: z.boolean().optional().default(false),
});

// ---------------------------------------------------------------------------
// Token refresh response — returned by POST /api/auth/v1/token/refresh/
// ---------------------------------------------------------------------------

export const TokenRefreshResponseSchema = z.object({
  access_token: z.string(),
});

// ---------------------------------------------------------------------------
// Me response — returned by GET /api/auth/v1/me/
// ---------------------------------------------------------------------------

export const MeResponseSchema = z.object({
  user: UserSchema,
});

// ---------------------------------------------------------------------------
// Register response
// ---------------------------------------------------------------------------

export const RegisterResponseSchema = z.object({
  id: z.number(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  phone: z.string().optional(),
  organization_saved: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Social login attempt — kept in sessionStorage between the provider redirect
// and the callback. `codeVerifier` is the PKCE verifier (RFC 7636).
// ---------------------------------------------------------------------------

export const SocialProviderSchema = z.enum(['google', 'github']);

export const SocialAuthAttemptSchema = z.object({
  state: z.string().min(1),
  provider: SocialProviderSchema,
  codeVerifier: z.string().regex(/^[A-Za-z0-9\-._~]{43,128}$/),
});

// ---------------------------------------------------------------------------
// API envelope — all m-ai-check-api responses are wrapped in this shape
// ---------------------------------------------------------------------------

export const ApiEnvelopeSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema,
    message: z.string(),
  });
